import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentManagementConsolidatedModal from '../../components/offensive/AgentManagementConsolidatedModal';
import FindingsManagementModal from '../../components/offensive/FindingsManagementModal';
import ReportEditorModal from '../../components/offensive/ReportEditorModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import offensiveApi from '../../services/offensiveApi';

// MITRE ATT&CK Tactics Data
const MITRE_TACTICS = [
  { key: 'recon', name: 'Reconnaissance', icon: 'Binoculars' },
  { key: 'resource', name: 'Resource Development', icon: 'Puzzle' },
  { key: 'initial', name: 'Initial Access', icon: 'Keyhole' },
  { key: 'exec', name: 'Execution', icon: 'Terminal' },
  { key: 'persist', name: 'Persistence', icon: 'Puzzle' },
  { key: 'privilege', name: 'Privilege Escalation', icon: 'Zap' },
  { key: 'defense', name: 'Defense Evasion', icon: 'Shield' },
  { key: 'credential', name: 'Credential Access', icon: 'Keyhole' },
  { key: 'discovery', name: 'Discovery', icon: 'Binoculars' },
  { key: 'lateral', name: 'Lateral Movement', icon: 'Network' },
  { key: 'collection', name: 'Collection', icon: 'Database' },
  { key: 'command', name: 'Command and Control', icon: 'Terminal' },
  { key: 'exfil', name: 'Exfiltration', icon: 'Database' },
  { key: 'impact', name: 'Impact', icon: 'Zap' },
];

// Simple SVG Icons
const TacticIcon = ({ type, className = "w-4 h-4" }) => {
  const icons = {
    Binoculars: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="8" cy="13" r="3" />
        <circle cx="16" cy="13" r="3" />
        <path d="M5 13V9l2-3h3l1 2h2l1-2h3l2 3v4" />
      </svg>
    ),
    Keyhole: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 3a5 5 0 0 0-2 9v3l2 2 2-2v-3a5 5 0 0 0-2-9z" />
      </svg>
    ),
    Terminal: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 5h16v14H4z" />
        <path d="m7 9 3 3-3 3" />
        <path d="M13 15h4" />
      </svg>
    ),
    Puzzle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M10 5a2 2 0 1 1 4 0h3v4h-2a2 2 0 1 0 0 4h2v4h-4v-2a2 2 0 1 0-4 0v2H5v-4h2a2 2 0 1 0 0-4H5V5z" />
      </svg>
    ),
    Network: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 5v4M12 15v4M5 12h4M15 12h4M6.5 6.5l2.5 2.5M14.9 14.9l2.6 2.6M6.5 17.5l2.5-2.5M14.9 9.1l2.6-2.6" />
      </svg>
    ),
    Database: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <ellipse cx="12" cy="6" rx="6" ry="3" />
        <path d="M6 6v6c0 1.7 2.7 3 6 3s6-1.3 6-3V6" />
      </svg>
    ),
    Zap: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M13 3 4 14h7l-1 7 9-11h-7z" />
      </svg>
    ),
    Shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  };
  return icons[type] || null;
};

function OffensiveEngagementsTable({
  title,
  engagements,
  selectedItems,
  currentPage = 1,
  itemsPerPage = 10,
  onDeleteEngagement,
  onCreateNew,
  onEditEngagement,
  customers,
  subservices,
  // Where a row leads, and what that destination is called. Wireless
  // engagements open their own testing console rather than the offensive
  // operations view.
  detailPath = '/offensive/operations',
  detailLabel = 'Testing',
}) {
  const navigate = useNavigate();
  const [selectAll, setSelectAll] = useState(false);
  const [isCheck, setIsCheck] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [engagementToDelete, setEngagementToDelete] = useState(null);
  const [showAgentManagementModal, setShowAgentManagementModal] = useState(false);
  const [selectedEngagementForManagement, setSelectedEngagementForManagement] = useState(null);
  const [showFindingsModal, setShowFindingsModal] = useState(false);
  const [selectedEngagementForFindings, setSelectedEngagementForFindings] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportGenerationError, setReportGenerationError] = useState(null);
  const [showReportEditor, setShowReportEditor] = useState(false);
  const [selectedEngagementForReport, setSelectedEngagementForReport] = useState(null);
  
  // Loading states
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [paginatedEngagements, setPaginatedEngagements] = useState([]);

  // Helper function to safely parse JSON
  const safeJsonParse = (jsonString, defaultValue = []) => {
    if (!jsonString) return defaultValue;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse JSON:', jsonString);
      return defaultValue;
    }
  };

  // Effect to handle pagination
  useEffect(() => {
    // If we receive data for the first time, we're no longer in initial load
    if (engagements.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
    
    // Calculate pagination
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setPaginatedEngagements(engagements.slice(start, end));
  }, [engagements, currentPage, itemsPerPage, isInitialLoad]);

  // Handle selection
  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setIsCheck(paginatedEngagements.map(eng => eng.id));
    } else {
      setIsCheck([]);
    }
  };

  const handleClick = (e) => {
    const { id, checked } = e.target;
    const numId = parseInt(id);
    setSelectAll(false);
    if (checked) {
      setIsCheck([...isCheck, numId]);
    } else {
      setIsCheck(isCheck.filter(item => item !== numId));
    }
  };

  // Update parent component with selected items
  useEffect(() => {
    if (selectedItems) {
      selectedItems(isCheck);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheck]);

  // Status badge colors
  const getStatusBadge = (status) => {
    const statusMap = {
      'not started': 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300',
      'in progress': 'bg-sky-50 dark:bg-sky-900/30 text-amber-500 dark:text-indigo-400',
      'finished': 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      'paused': 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300'
    };
    return statusMap[status] || 'bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300';
  };

  // Get progress bar color based on percentage
  const getProgressColor = (progress) => {
    if (progress >= 90) return 'bg-emerald-600';
    if (progress >= 70) return 'bg-teal-600';
    if (progress >= 50) return 'bg-amber-600';
    return 'bg-rose-600';
  };

  // Handle row click to navigate to the engagement's detail view
  const handleRowClick = (engagementId) => {
    navigate(`${detailPath}/${engagementId}`);
  };

  // Toggle expanded row
  const toggleExpand = (e, engagementId) => {
    e.stopPropagation();
    setExpandedRow(expandedRow === engagementId ? null : engagementId);
  };

  // Show loading only during initial load when no data is available
  const showLoading = isInitialLoad && engagements.length === 0;

  // Handle report generation
  const handleGenerateReport = async (e, engagement) => {
    e.stopPropagation();

    setIsGeneratingReport(true);
    setReportGenerationError(null);

    try {
      // Fetch findings count if not available
      let findingsCount = engagement.findings_count;
      if (!findingsCount) {
        try {
          const findings = await offensiveApi.getOffensiveFindings(engagement.id);
          findingsCount = findings?.length || 0;
        } catch (err) {
          console.warn('Could not fetch findings count:', err);
        }
      }

      // Check prerequisites (but allow backend to validate as well)
      if (!engagement.engagement_code && !engagement.code) {
        setReportGenerationError('Engagement has no assigned code. Please contact an administrator.');
        setTimeout(() => setReportGenerationError(null), 5000);
        setIsGeneratingReport(false);
        return;
      }

      const blob = await offensiveApi.generateReport(engagement.id);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      const engagementCode = engagement.engagement_code || engagement.code || `engagement_${engagement.id}`;
      a.download = `report_${engagementCode}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Show success message (you might want to use a toast notification here)
      console.log('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Error generating report. Please try again.';
      setReportGenerationError(errorMessage);
      setTimeout(() => setReportGenerationError(null), 10000);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      <header className="px-5 py-4">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
        {showLoading && (
          <div className="text-xs text-slate-500 mt-1">
            Loading...
          </div>
        )}
        {reportGenerationError && (
          <div className="mt-2 p-3 rounded-lg bg-rose-900/30 border border-rose-800 text-rose-300 text-sm">
            {reportGenerationError}
          </div>
        )}
      </header>
      <div>
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300">
            {/* Table header */}
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                  <div className="flex items-center">
                    <label className="inline-flex">
                      <span className="sr-only">Select all</span>
                      <input 
                        className="form-checkbox" 
                        type="checkbox" 
                        checked={selectAll} 
                        onChange={handleSelectAll} 
                      />
                    </label>
                  </div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Reference</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Org.</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Type</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Status</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Progress</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Findings</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {showLoading ? (
                <tr>
                  <td colSpan="8" className="px-2 first:pl-5 last:pr-5 py-8">
                    <div className="flex items-center justify-center">
                      <LoadingSpinner />
                      <span className="ml-2 text-slate-400">
                        Loading...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : paginatedEngagements.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <p className="text-center py-4">
                      No engagements found.{" "}
                      <a 
                        href="#" 
                        className="text-indigo-500 hover:text-indigo-600" 
                        onClick={(e) => {
                          e.preventDefault();
                          onCreateNew();
                        }}
                      >
                        Create one to get started
                      </a>
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedEngagements.map((engagement) => (
                  <React.Fragment key={engagement.id}>
                    <tr 
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30"
                      onClick={() => handleRowClick(engagement.id)}
                    >
                      {/* Checkbox */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <label className="inline-flex">
                            <span className="sr-only">Select</span>
                            <input 
                              id={engagement.id} 
                              className="form-checkbox" 
                              type="checkbox" 
                              onChange={handleClick} 
                              checked={isCheck.includes(engagement.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </label>
                        </div>
                      </td>

                      {/* Reference (Title) */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 min-w-[200px] max-w-[200px]">
                        <div className="overflow-hidden">
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200 block truncate" title={engagement.title}>
                            {engagement.title}
                          </span>
                        </div>
                      </td>

                      {/* Organization */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                            {engagement.customer?.short_name || customers[engagement.customer_id]?.[1] || 'n/a'}
                          </span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="inline-flex items-center border-l-2 border-slate-300 dark:border-slate-600 pl-3">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {subservices.find(s => s.id === engagement.subtype_id)?.name || 
                             subservices[engagement.subtype_id]?.[0] || 'n/a'}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 capitalize ${getStatusBadge(engagement.status)}`}>
                          {engagement.status}
                        </div>
                      </td>

                      {/* Progress */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-wrap">
                        <div className="inline-flex items-center justify-center px-3 py-1 font-medium border-l-4 border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                          {engagement.progress || 0}%
                        </div>
                      </td>

                      {/* Findings Count */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-wrap">
                        <div className={`inline-flex items-center justify-center px-3 py-1 font-medium border-l-4 relative w-20 ${
                          (engagement.findings_count || 0) >= 10
                            ? 'border-rose-600 text-rose-700 dark:text-rose-300'
                            : (engagement.findings_count || 0) >= 5
                              ? 'border-amber-600 text-amber-700 dark:text-amber-300'
                              : 'border-emerald-600 text-emerald-700 dark:text-emerald-300'
                        }`}>
                          {engagement.findings_count || 0}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                        <div className="flex items-center">
                          <button
                            className={`text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 ${expandedRow === engagement.id && 'rotate-180'}`}
                            aria-expanded={expandedRow === engagement.id}
                            onClick={(e) => toggleExpand(e, engagement.id)}
                            aria-controls={`description-${engagement.id}`}
                          >
                            <span className="sr-only">Expand</span>
                            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                              <path d="M16 20l-5.4-5.4 1.4-1.4 4 4 4-4 1.4 1.4z" />
                            </svg>
                          </button>
                          <button
                            className={`rounded-full ${engagement.status === 'in progress' ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' : 'text-indigo-500 hover:text-indigo-600'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (engagement.status !== 'in progress' && onEditEngagement) {
                                onEditEngagement(engagement);
                              }
                            }}
                            disabled={engagement.status === 'in progress'}
                            title={engagement.status === 'in progress' ? 'Cannot edit a running engagement' : 'Edit Engagement'}
                          >
                            <span className="sr-only">Edit</span>
                            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                              <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
                            </svg>
                          </button>
                          <button 
                            className="text-rose-500 hover:text-rose-600 rounded-full" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setEngagementToDelete(engagement.id);
                              setShowDeleteModal(true);
                            }}
                          >
                            <span className="sr-only">Delete</span>
                            <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                              <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                              <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row */}
                    {expandedRow === engagement.id && (
                      <tr id={`description-${engagement.id}`} role="region">
                        <td colSpan="7" className="px-2 first:pl-5 last:pr-5 py-3">
                          <div className="bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900/50 dark:via-slate-800/30 dark:to-slate-900/50 p-6 rounded-lg border border-slate-200/50 dark:border-slate-700/50 shadow-sm">
                            
                            {/* Header Section */}
                            <div className="mb-6 pb-4 border-b border-slate-300 dark:border-slate-700">
                              <div>
                                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Engagement Configuration</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Technical details and operational settings</p>
                              </div>
                            </div>

                            {/* Action Buttons Row */}
                            <div className="flex gap-2 mb-6">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEngagementForManagement(engagement);
                                  setShowAgentManagementModal(true);
                                }}
                                className="group flex-1 flex items-center justify-center gap-2 rounded-lg bg-violet-500/10 dark:bg-violet-500/15 border border-violet-500/20 dark:border-violet-500/30 px-3 py-2.5 hover:border-violet-500/50 hover:bg-violet-500/20 transition-all"
                              >
                                <svg className="w-4 h-4 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                </svg>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">Agents</span>
                              </button>

                              <button
                                onClick={() => handleRowClick(engagement.id)}
                                className="group flex-1 flex items-center justify-center gap-2 rounded-lg bg-violet-500/10 dark:bg-violet-500/15 border border-violet-500/20 dark:border-violet-500/30 px-3 py-2.5 hover:border-violet-500/50 hover:bg-violet-500/20 transition-all"
                              >
                                <svg className="w-4 h-4 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">{detailLabel}</span>
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEngagementForFindings(engagement);
                                  setShowFindingsModal(true);
                                }}
                                className="group flex-1 flex items-center justify-center gap-2 rounded-lg bg-violet-500/10 dark:bg-violet-500/15 border border-violet-500/20 dark:border-violet-500/30 px-3 py-2.5 hover:border-violet-500/50 hover:bg-violet-500/20 transition-all"
                              >
                                <svg className="w-4 h-4 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">Findings</span>
                              </button>

                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedEngagementForReport(engagement); setShowReportEditor(true); }}
                                className="group flex-1 flex items-center justify-center gap-2 rounded-lg bg-violet-500/10 dark:bg-violet-500/15 border border-violet-500/20 dark:border-violet-500/30 px-3 py-2.5 hover:border-violet-500/50 hover:bg-violet-500/20 transition-all"
                              >
                                <svg className="w-4 h-4 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">Edit Report</span>
                              </button>

                              <button
                                onClick={(e) => handleGenerateReport(e, engagement)}
                                disabled={isGeneratingReport}
                                className="group flex-1 flex items-center justify-center gap-2 rounded-lg bg-violet-500/10 dark:bg-violet-500/15 border border-violet-500/20 dark:border-violet-500/30 px-3 py-2.5 hover:border-violet-500/50 hover:bg-violet-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isGeneratingReport ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <svg className="w-4 h-4 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">
                                  {isGeneratingReport ? 'Generating...' : 'Generate PDF'}
                                </span>
                              </button>
                            </div>

                            {/* Info Cards Grid */}
                            <div className="grid grid-cols-3 gap-4 mb-4">
                              
                              {/* Scope Card */}
                              <div className="bg-white dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700 shadow-sm">
                                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">Scope & Depth</h4>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 dark:bg-slate-900/30">
                                    <span className="text-xs text-slate-600 dark:text-slate-400">Targets</span>
                                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                      {safeJsonParse(engagement.offensive_scope?.external_targets).length +
                                       safeJsonParse(engagement.offensive_scope?.target_networks).length +
                                       safeJsonParse(engagement.offensive_scope?.target_domains).length +
                                       safeJsonParse(engagement.offensive_scope?.target_applications).length}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 dark:bg-slate-900/30">
                                    <span className="text-xs text-slate-600 dark:text-slate-400">Excluded</span>
                                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                      {safeJsonParse(engagement.offensive_scope?.excluded_targets).length}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1.5 px-2 rounded bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50">
                                    <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">Framework</span>
                                    <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400">
                                      {engagement.offensive_scope?.offensive_framework?.toUpperCase().replace('_', ' ') || 'MITRE ATT&CK'}
                                    </span>
                                  </div>
                                  <div className="pt-1">
                                    <div className="flex items-center gap-2 py-1.5 px-2 rounded bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50">
                                      <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">Depth:</span>
                                      {(() => {
                                        const aiConfig = safeJsonParse(engagement.offensive_scope?.ai_config, {});
                                        const depth = aiConfig.max_depth;
                                        if (!depth || depth < 1 || depth > MITRE_TACTICS.length) {
                                          return <span className="text-xs text-slate-500 dark:text-slate-400">n/a</span>;
                                        }
                                        const tactic = MITRE_TACTICS[depth - 1];
                                        return (
                                          <span className="inline-flex items-center gap-1.5">
                                            <TacticIcon type={tactic.icon} className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                                            <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400">{tactic.name}</span>
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Timeline Card */}
                              <div className="bg-white dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700 shadow-sm">
                                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">Timeline & Execution</h4>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 dark:bg-slate-900/30">
                                    <span className="text-xs text-slate-600 dark:text-slate-400">Start</span>
                                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                      {engagement.start ? new Date(engagement.start).toLocaleDateString() : 'n/a'}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 dark:bg-slate-900/30">
                                    <span className="text-xs text-slate-600 dark:text-slate-400">End</span>
                                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                      {engagement.finish ? new Date(engagement.finish).toLocaleDateString() : 'n/a'}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-100 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-600/50">
                                    <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">Mode</span>
                                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-slate-200">
                                      {engagement.offensive_scope?.ai_autonomous ? (
                                        <>
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                          </svg>
                                          Autonomous
                                        </>
                                      ) : (
                                        <>
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                          </svg>
                                          Manual
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Description Card */}
                              <div className="bg-white dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700 shadow-sm">
                                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">Description</h4>
                                <div className="bg-slate-50 dark:bg-slate-900/30 rounded p-3 h-[120px] overflow-y-auto">
                                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                                    {engagement.description || (
                                      <span className="text-slate-400 dark:text-slate-500 italic">No description available</span>
                                    )}
                                  </p>
                                </div>
                              </div>

                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => {
              setShowDeleteModal(false);
              setEngagementToDelete(null);
            }}
          ></div>
          <div className="relative max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 fill-current text-amber-500" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Are you sure you want to delete this engagement? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                onClick={() => {
                  setShowDeleteModal(false);
                  setEngagementToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 transition-colors"
                onClick={() => {
                  onDeleteEngagement([engagementToDelete]);
                  setShowDeleteModal(false);
                  setEngagementToDelete(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Management Consolidated Modal */}
      {showAgentManagementModal && selectedEngagementForManagement && (
        <AgentManagementConsolidatedModal
          engagement={selectedEngagementForManagement}
          onClose={() => {
            setShowAgentManagementModal(false);
            setSelectedEngagementForManagement(null);
          }}
          onAgentBuilt={(agentData) => {
            console.log('Agent built:', agentData);
            // Modal will handle refreshing the list
          }}
        />
      )}

      {/* Findings Management Modal */}
      {showFindingsModal && selectedEngagementForFindings && (
        <FindingsManagementModal
          engagement={selectedEngagementForFindings}
          onClose={() => {
            setShowFindingsModal(false);
            setSelectedEngagementForFindings(null);
          }}
          onFindingsUpdated={() => {
            console.log('Findings updated successfully');
          }}
        />
      )}

      {/* Report Editor Modal */}
      {showReportEditor && selectedEngagementForReport && (
        <ReportEditorModal
          engagement={selectedEngagementForReport}
          onClose={() => {
            setShowReportEditor(false);
            setSelectedEngagementForReport(null);
          }}
        />
      )}
    </div>
  );
}

export default OffensiveEngagementsTable;

